// shared/utils/validation.js
// Form validation utilities

/**
 * Validate email format
 * @param {string} email 
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 * @param {string} password 
 * @returns {Object} {isValid, errors}
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 6) {
    errors.push('Password must be at least 6 characters long');
  }
  
  if (password.length < 8) {
    errors.push('Consider using at least 8 characters for better security');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Consider adding an uppercase letter');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Consider adding a number');
  }
  
  return {
    isValid: password.length >= 6,
    errors,
    strength: getPasswordStrength(password)
  };
};

/**
 * Get password strength
 * @param {string} password 
 * @returns {string} 'weak', 'medium', 'strong'
 */
export const getPasswordStrength = (password) => {
  let strength = 0;
  
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;
  
  if (strength <= 2) return 'weak';
  if (strength <= 4) return 'medium';
  return 'strong';
};

/**
 * Validate name (letters, spaces, hyphens only)
 * @param {string} name 
 * @returns {boolean}
 */
export const isValidName = (name) => {
  const nameRegex = /^[a-zA-Z\s-]+$/;
  return nameRegex.test(name) && name.trim().length >= 2;
};

/**
 * Sanitize user input (remove potentially harmful characters)
 * @param {string} input 
 * @returns {string}
 */
export const sanitizeInput = (input) => {
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .trim();
};

/**
 * Validate journal entry
 * @param {string} content 
 * @returns {Object} {isValid, wordCount, errors}
 */
export const validateJournalEntry = (content) => {
  const errors = [];
  const wordCount = content.trim().split(/\s+/).filter(word => word.length > 0).length;
  
  if (content.trim().length === 0) {
    errors.push('Journal entry cannot be empty');
  }
  
  if (wordCount < 10) {
    errors.push('Please write at least 10 words');
  }
  
  return {
    isValid: content.trim().length > 0 && wordCount >= 10,
    wordCount,
    errors
  };
};

/**
 * Count words in text
 * @param {string} text 
 * @returns {number}
 */
export const countWords = (text) => {
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Validate URL format
 * @param {string} url 
 * @returns {boolean}
 */
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate phone number (basic)
 * @param {string} phone 
 * @returns {boolean}
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
  return phoneRegex.test(phone);
};

/**
 * Check if string is empty or whitespace
 * @param {string} str 
 * @returns {boolean}
 */
export const isEmpty = (str) => {
  return !str || str.trim().length === 0;
};

/**
 * Validate age (must be 13 or older)
 * @param {number} age 
 * @returns {Object} {isValid, error}
 */
export const validateAge = (age) => {
  if (age < 13) {
    return {
      isValid: false,
      error: 'You must be at least 13 years old to use this app'
    };
  }
  
  if (age > 120) {
    return {
      isValid: false,
      error: 'Please enter a valid age'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate required fields in an object
 * @param {Object} data 
 * @param {Array<string>} requiredFields 
 * @returns {Object} {isValid, missingFields}
 */
export const validateRequiredFields = (data, requiredFields) => {
  const missingFields = requiredFields.filter(field => 
    !data[field] || isEmpty(String(data[field]))
  );
  
  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};
